import React, { memo, useMemo } from "react";
import PropTypes from "prop-types";

const ACTION_STATUS = Object.freeze({
  PENDING: "pending",
  EXECUTING: "executing",
  COMPLETED: "completed",
  FAILED: "failed"
});

const STATUS_CONFIG = Object.freeze({
  [ACTION_STATUS.PENDING]: {
    color: "#92400E",
    background: "#FEF3C7",
    borderColor: "#FDE68A",
    label: "Waiting for approval",
    icon: "⏳"
  },

  [ACTION_STATUS.EXECUTING]: {
    color: "#1D4ED8",
    background: "#DBEAFE",
    borderColor: "#BFDBFE",
    label: "Executing",
    icon: "⚙️"
  },

  [ACTION_STATUS.COMPLETED]: {
    color: "#047857",
    background: "#D1FAE5",
    borderColor: "#A7F3D0",
    label: "Completed",
    icon: "✓"
  },

  [ACTION_STATUS.FAILED]: {
    color: "#B91C1C",
    background: "#FEE2E2",
    borderColor: "#FECACA",
    label: "Failed",
    icon: "!"
  }
});

function normalizeStatus(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  return Object.prototype.hasOwnProperty.call(
    STATUS_CONFIG,
    normalized
  )
    ? normalized
    : ACTION_STATUS.PENDING;
}

function humanizeKey(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function safeStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "[Unable to display value]";
  }
}

function prettify(value) {
  if (value === null || value === undefined) {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "string") {
    return value.trim() || "—";
  }

  if (typeof value === "number") {
    return Number.isFinite(value)
      ? String(value)
      : "—";
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "—";
    }

    const containsComplexValues = value.some(
      (item) =>
        item !== null &&
        typeof item === "object"
    );

    return containsComplexValues
      ? safeStringify(value)
      : value.map(prettify).join(", ");
  }

  if (typeof value === "object") {
    return safeStringify(value);
  }

  return String(value);
}

function resolveToolName(action) {
  return (
    action?.tool?.displayName ||
    action?.tool?.name ||
    action?.displayName ||
    action?.label ||
    action?.name ||
    "Copilot action"
  );
}

function resolveDescription(action, status) {
  if (
    status === ACTION_STATUS.COMPLETED &&
    action?.result?.message
  ) {
    return action.result.message;
  }

  if (
    status === ACTION_STATUS.FAILED &&
    action?.error?.message
  ) {
    return action.error.message;
  }

  return (
    action?.confirmation?.message ||
    action?.description ||
    "This action will modify your ESG workspace."
  );
}

function resolveArguments(action) {
  const value =
    action?.arguments ||
    action?.toolArguments ||
    action?.params ||
    action?.input ||
    {};

  return value &&
    typeof value === "object" &&
    !Array.isArray(value)
    ? value
    : {};
}

function resolveResultDetails(action) {
  const result = action?.result;

  if (
    !result ||
    typeof result !== "object" ||
    Array.isArray(result)
  ) {
    return {};
  }

  const excludedKeys = new Set([
    "status",
    "action",
    "message",
    "executiveSummary",
    "saved",
    "model",
    "updatedAt",
    "createdAt"
  ]);

  return Object.fromEntries(
    Object.entries(result).filter(
      ([key, value]) =>
        !excludedKeys.has(key) &&
        value !== undefined &&
        value !== null &&
        value !== ""
    )
  );
}

function DetailTable({
  title,
  values
}) {
  const entries = Object.entries(values || {});

  if (entries.length === 0) {
    return null;
  }

  return (
    <section
      aria-label={title}
      style={{
        marginTop: 18
      }}
    >
      <div
        style={{
          color: "#111827",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.01em",
          marginBottom: 8
        }}
      >
        {title}
      </div>

      <div
        style={{
          border: "1px solid #E5E7EB",
          borderRadius: 12,
          overflow: "hidden"
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed"
          }}
        >
          <tbody>
            {entries.map(
              ([key, value], index) => (
                <tr
                  key={key}
                  style={{
                    borderTop:
                      index === 0
                        ? "none"
                        : "1px solid #E5E7EB"
                  }}
                >
                  <th
                    scope="row"
                    style={{
                      width: "38%",
                      padding: "11px 14px",
                      background: "#F9FAFB",
                      color: "#4B5563",
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: 1.45,
                      textAlign: "left",
                      verticalAlign: "top",
                      wordBreak: "break-word"
                    }}
                  >
                    {humanizeKey(key)}
                  </th>

                  <td
                    style={{
                      padding: "11px 14px",
                      color: "#111827",
                      fontSize: 13,
                      lineHeight: 1.55,
                      verticalAlign: "top",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere"
                    }}
                  >
                    {prettify(value)}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

DetailTable.propTypes = {
  title: PropTypes.string.isRequired,
  values: PropTypes.object
};

DetailTable.defaultProps = {
  values: {}
};

function PendingActionCard({
  action,
  status = ACTION_STATUS.PENDING,
  className = ""
}) {
  const normalizedStatus =
    normalizeStatus(status);

  const current =
    STATUS_CONFIG[normalizedStatus];

  const toolName = useMemo(
    () => resolveToolName(action),
    [action]
  );

  const description = useMemo(
    () =>
      resolveDescription(
        action,
        normalizedStatus
      ),
    [action, normalizedStatus]
  );

  const parameters = useMemo(
    () => resolveArguments(action),
    [action]
  );

  const resultDetails = useMemo(
    () => resolveResultDetails(action),
    [action]
  );

  if (!action) {
    return null;
  }

  const isExecuting =
    normalizedStatus ===
    ACTION_STATUS.EXECUTING;

  const isCompleted =
    normalizedStatus ===
    ACTION_STATUS.COMPLETED;

  const isFailed =
    normalizedStatus ===
    ACTION_STATUS.FAILED;

  return (
    <article
      className={className}
      aria-live="polite"
      aria-busy={isExecuting}
      data-action-status={normalizedStatus}
      style={{
        width: "100%",
        boxSizing: "border-box",
        border: `1px solid ${current.borderColor}`,
        borderRadius: 16,
        background: "#FFFFFF",
        padding: 20,
        marginBottom: 20,
        boxShadow:
          "0 4px 14px rgba(15, 23, 42, 0.06)",
        transition:
          "border-color 180ms ease, box-shadow 180ms ease"
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            minWidth: 0
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 38,
              height: 38,
              flex: "0 0 38px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #D1FAE5",
              borderRadius: 11,
              background: "#ECFDF5",
              color: "#047857",
              fontSize: 18
            }}
          >
            📄
          </div>

          <div
            style={{
              minWidth: 0,
              paddingTop: 1
            }}
          >
            <div
              style={{
                color: "#111827",
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1.35,
                overflowWrap: "anywhere"
              }}
            >
              {toolName}
            </div>

            <div
              style={{
                color: "#6B7280",
                fontSize: 12,
                lineHeight: 1.5,
                marginTop: 3
              }}
            >
              Ecovanta Copilot action
            </div>
          </div>
        </div>

        <span
          role="status"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            flex: "0 0 auto",
            padding: "6px 10px",
            border: `1px solid ${current.borderColor}`,
            borderRadius: 999,
            background: current.background,
            color: current.color,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.2,
            whiteSpace: "nowrap"
          }}
        >
          <span aria-hidden="true">
            {current.icon}
          </span>

          {current.label}
        </span>
      </header>

      {isExecuting && (
        <div
          aria-hidden="true"
          style={{
            height: 3,
            marginTop: 16,
            borderRadius: 999,
            background:
              "linear-gradient(90deg, #DBEAFE 0%, #2563EB 50%, #DBEAFE 100%)",
            backgroundSize: "200% 100%",
            animation:
              "copilotActionProgress 1.4s linear infinite"
          }}
        />
      )}

      <div
        style={{
          color:
            isFailed ? "#991B1B" : "#4B5563",
          fontSize: 14,
          lineHeight: 1.65,
          marginTop: 16,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere"
        }}
      >
        {description}
      </div>

      <DetailTable
        title="Parameters"
        values={parameters}
      />

      {isCompleted && (
        <DetailTable
          title="Result"
          values={resultDetails}
        />
      )}

      {isCompleted && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
            padding: "10px 12px",
            border: "1px solid #A7F3D0",
            borderRadius: 10,
            background: "#ECFDF5",
            color: "#065F46",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.5
          }}
        >
          <span aria-hidden="true">✓</span>

          The action completed successfully.
        </div>
      )}

      {isFailed && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            marginTop: 16,
            padding: "10px 12px",
            border: "1px solid #FECACA",
            borderRadius: 10,
            background: "#FEF2F2",
            color: "#991B1B",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.5
          }}
        >
          <span aria-hidden="true">!</span>

          <span>
            The action could not be completed.
            Review the error and try again.
          </span>
        </div>
      )}

      <style>
        {`
          @keyframes copilotActionProgress {
            0% {
              background-position: 200% 0;
            }

            100% {
              background-position: -200% 0;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            [data-action-status="executing"] {
              scroll-behavior: auto;
            }
          }
        `}
      </style>
    </article>
  );
}

PendingActionCard.propTypes = {
  action: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    displayName: PropTypes.string,
    description: PropTypes.string,

    tool: PropTypes.shape({
      name: PropTypes.string,
      displayName: PropTypes.string
    }),

    confirmation: PropTypes.shape({
      message: PropTypes.string
    }),

    arguments: PropTypes.object,
    toolArguments: PropTypes.object,
    params: PropTypes.object,
    input: PropTypes.object,

    result: PropTypes.shape({
      status: PropTypes.string,
      action: PropTypes.string,
      message: PropTypes.string
    }),

    error: PropTypes.shape({
      message: PropTypes.string
    })
  }),

  status: PropTypes.oneOf(
    Object.values(ACTION_STATUS)
  ),

  className: PropTypes.string
};

PendingActionCard.defaultProps = {
  action: null,
  status: ACTION_STATUS.PENDING,
  className: ""
};

export {
  ACTION_STATUS
};

export default memo(PendingActionCard);